import { useEffect, useState } from 'react'
import type { WalletBalance } from '../types/wallet'
import { fetchWalletBalances } from '../api/wallet'
import { MINTS, SYMBOLS } from '../api/trade'

/**
 * Light-weight, Trade-page-only balance fetcher. Polls every 15 s.
 * We deliberately do NOT share the Dashboard's WS stream here — that hook
 * has its own consumers and lifecycle; duplicate polling in this view is
 * cheap and keeps coupling low.
 */
export function useWalletBalances(portfolioId: string | null, intervalMs = 15_000): WalletBalance[] | null {
  const [balances, setBalances] = useState<WalletBalance[] | null>(null)

  useEffect(() => {
    if (!portfolioId) { setBalances(null); return }
    let cancelled = false
    const run = () => {
      fetchWalletBalances(portfolioId)
        .then(data => { if (!cancelled) setBalances(data) })
        .catch(() => { /* silent — retained last good data */ })
    }
    run()
    const id = setInterval(run, intervalMs)
    return () => { cancelled = true; clearInterval(id) }
  }, [portfolioId, intervalMs])

  return balances
}

export interface BalanceView {
  amount: string
  usdValue: string
  priceUsd: string
  symbol: string
  mint: string
  decimals: number
}

/** Resolve a balance entry by mint, with SOL/USDC/jupSOL fallback aliasing. */
export function findBalance(balances: WalletBalance[] | null, mint: string): BalanceView | null {
  if (!balances) return null
  const sym = SYMBOLS[mint] ?? ''
  const hit = balances.find(b =>
    b.mintAddress === mint
    || (b.symbol && sym && b.symbol.toLowerCase() === sym.toLowerCase())
  )
  if (!hit) return null
  const decimals =
    mint === MINTS.USDC ? 6 :
    mint === MINTS.SOL  ? 9 :
    mint === MINTS.jupSOL ? 9 : 9
  return {
    amount: hit.amount,
    usdValue: hit.usdValue,
    priceUsd: hit.priceUsd,
    symbol: hit.symbol || sym,
    mint: hit.mintAddress ?? mint,
    decimals,
  }
}

export function formatBalance(value: string, maxFrac = 4): string {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { maximumFractionDigits: maxFrac })
}

export function formatUsdShort(value: string): string {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return '—'
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}
